import { useState } from "react";
import { CreateModelVersionRequest, UpdateModelVersionRequest, ModelExtendedData, ExtendedDataNodeType } from "@api/apiClient";
import { useCreateModelVersion, useUpdateModelVersion } from "@api/models";
import useAppStore from "@hooks/useAppStore";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { MutableRefObject } from "react";

/**
 * useModelEdit
 * - collects ExtendedData from the grid
 * - includes hasChildMultiplierOverride flag when present
 */

export interface UseModelEditProps {
  modelGridApiRef: MutableRefObject<GridApiPremium>;
  modifiedRows: Set<string>;
  setModifiedRows: (rows: Set<string>) => void;
}

export const useModelEdit = ({ modelGridApiRef, modifiedRows, setModifiedRows }: UseModelEditProps) => {
  const appStore = useAppStore();
  const [isCreatingVersion, setIsCreatingVersion] = useState(false);
  const [isUpdatingVersion, setIsUpdatingVersion] = useState(false);

  const createModelVersionMutation = useCreateModelVersion(appStore.model?.modelId);
  const updateModelVersionMutation = useUpdateModelVersion(appStore.model?.modelId);

  // Helper function to check if a row has any non-default values that indicate changes
  const hasRowChanges = (row: any): boolean => {
    const hasMultiplierInput = row.data?.multiplierInput && row.data.multiplierInput.trim() !== "";
    const hasMultiplierValue = row.data?.multiplierValue !== null && row.data?.multiplierValue !== undefined;
    const hasNotes = row.data?.notes && row.data.notes.trim() !== "";
    const hasSystemId = row.data?.systemId && row.data.systemId.trim() !== "";
    const hasSubSystemId = row.data?.subSystemId && row.data.subSystemId.trim() !== "";
    const hasAttribute1 = row.data?.attribute1 && row.data?.attribute1.trim() !== "";
    const hasAttribute2 = row.data?.attribute2 && row.data?.attribute2.trim() !== "";
    const hasAttribute3 = row.data?.attribute3 && row.data?.attribute3.trim() !== "";
    const hasChildOverride = row.data?.hasChildMultiplierOverride === true;

    const hasCarbonInput = row.data?.carbonImpactInput && row.data.carbonImpactInput.trim() !== "";
    const hasCarbonValue = row.data?.carbonImpactValue !== null && row.data?.carbonImpactValue !== undefined;
    const hasCarbonNotes = row.data?.carbonImpactNotes && row.data.carbonImpactNotes.trim() !== "";
    const hasChildCarbonOverride = row.data?.hasChildCarbonImpactOverride === true;

    return hasMultiplierInput || hasMultiplierValue || hasNotes || hasSystemId ||
           hasSubSystemId || hasAttribute1 || hasAttribute2 || hasAttribute3 || hasChildOverride ||
           hasCarbonInput ||  hasCarbonValue || hasCarbonNotes || hasChildCarbonOverride;
  };

  // Helper function to convert row type to ExtendedDataNodeType
  const convertRowToModelExtendedData = (row: any): ModelExtendedData | null => {
    const isFamilyNode = row.type === "group" && row.key && row.key.includes("/");

    let nodeType: ExtendedDataNodeType;
    let nodeId: string;

    switch (row.type) {
      case "element":
        nodeType = ExtendedDataNodeType.Element;
        nodeId = row.data.elementId;
        break;
      case "component":
        nodeType = ExtendedDataNodeType.Component;
        nodeId = row.data.componentId;
        break;
      case "category":
        nodeType = ExtendedDataNodeType.Category;
        nodeId = row.key;
        break;
      case "group":
        if (!isFamilyNode) return null;
        nodeType = ExtendedDataNodeType.Family;
        nodeId = row.key;
        break;
      default:
        return null;
    }

    // Build the payload expected by backend ModelExtendedData DTO
    // Note: ModelExtendedData should contain only metadata fields, NOT quant changes
    return new ModelExtendedData({
      nodeId: nodeId,
      nodeType: nodeType,
      multiplierInput: row.data.multiplierInput ?? "",
      multiplierValue: row.data.multiplierValue ?? null,
      notes: row.data.notes ?? "",
      systemId: row.data.systemId ?? "",
      subSystemId: row.data.subSystemId ?? "",
      attribute1: row.data.attribute1 ?? "",
      attribute2: row.data.attribute2 ?? "",
      attribute3: row.data.attribute3 ?? "",
      hasChildMultiplierOverride: row.data.hasChildMultiplierOverride === true,
      carbonImpactInput: row.data.carbonImpactInput ?? "",
      carbonImpactValue: row.data.carbonImpactValue ?? null,
      carbonImpactNotes: row.data.carbonImpactNotes ?? "",
      hasChildCarbonImpactOverride: row.data.hasChildCarbonImpactOverride === true,
    } as any);
  };

  // Helper function to validate rows for errors
  const hasValidationErrors = (rows: any[]): boolean => {
    return rows.some(row => {
      const multiplierInput = row.data?.multiplierInput;
      const carbonInput = row.data?.carbonImpactInput;
      if (multiplierInput && multiplierInput.trim() !== "") {
        const multiplierValue = row.data?.multiplierValue;
        if (multiplierValue === null || multiplierValue === undefined || isNaN(multiplierValue)) {
          console.error("Validation error: multiplierInput provided but multiplierValue is invalid", row);
          return true;
        }
      }

      if (carbonInput && carbonInput.trim() !== "") {
        const carbonValue = row.data?.carbonImpactValue;
        if (carbonValue === null || carbonValue === undefined || isNaN(carbonValue)) {
          console.error("Validation error: carbonImpactInput provided but carbonImpactValue is invalid", row);
          return true;
        }
      }

      return false;
    });
  };

  // Helper function to collect extended data from grid
  const collectExtendedData = (): ModelExtendedData[] => {
    const allRowsMap = modelGridApiRef.current.getRowModels();
    const allRows = Array.from(allRowsMap.values());
    const modifiedRowsArray = Array.from(modifiedRows);

    const extendedData: ModelExtendedData[] = [];

    // Process all rows that have changes (including cascaded changes)
    for (const row of allRows) {
      const rowId = row.id?.toString();
      const isModified = rowId ? modifiedRowsArray.includes(rowId) : false;
      const hasChanges = hasRowChanges(row);

      if (isModified || hasChanges) {
        const modelExtendedData = convertRowToModelExtendedData(row);
        if (modelExtendedData) {
          extendedData.push(modelExtendedData);
        }
      }
    }

    return extendedData;
  };

  const handleCreateVersionSubmit = async (suffix: string, sourceModelId: string) => {
    if (!appStore.model) {
      console.error("No model available for version creation");
      return false;
    }

    try {
      setIsCreatingVersion(true);

      const rows = modelGridApiRef.current.getSortedRowIds().map((id) => modelGridApiRef.current.getRow(id));
      if (hasValidationErrors(rows)) {
        console.error("Validation errors found, cannot create version");
        return false;
      }

      const extendedData = collectExtendedData();

      const request = new CreateModelVersionRequest({
        projectId: appStore.model.projectId,
        sourceModelId: sourceModelId,
        suffix: suffix,
        extendedData: extendedData,
      } as any);

      await createModelVersionMutation.mutateAsync(request);

      // Clear modified rows after successful creation
      setModifiedRows(new Set());

      return true;
    } catch (error) {
      console.error("Error creating model version:", error);
      return false;
    } finally {
      setIsCreatingVersion(false);
    }
  };

  const handleUpdateVersion = async () => {
    if (!appStore.model) {
      console.error("No model available for version update");
      return false;
    }

    try {
      setIsUpdatingVersion(true);

      const rows = modelGridApiRef.current.getSortedRowIds().map((id) => modelGridApiRef.current.getRow(id));
      if (hasValidationErrors(rows)) {
        console.error("Validation errors found, cannot update version");
        return false;
      }

      const extendedData = collectExtendedData();

      const request = new UpdateModelVersionRequest({
        modelId: appStore.model.modelId,
        extendedData: extendedData,
      } as any);

      console.log("Updating version with request:", request);
      console.log("Extended data count:", extendedData.length);

      await updateModelVersionMutation.mutateAsync(request);

      // Clear modified rows after successful update
      setModifiedRows(new Set());

      return true;
    } catch (error) {
      console.error("Error updating model version:", error);
      return false;
    } finally {
      setIsUpdatingVersion(false);
    }
  };

  return {
    handleCreateVersionSubmit,
    handleUpdateVersion,
    isCreatingVersion,
    isUpdatingVersion,
    isLoading: isCreatingVersion || isUpdatingVersion,
    createModelVersionMutation,
    updateModelVersionMutation,
  };
};

export default useModelEdit;
